import { TOptions } from 'i18next'
import { GetDetailedTableQuery } from '~/common/generated/apollo-operations'
import { i18NextKeys } from '~/common/generated/i18n.generated'
import {
  type LookupFilterContextType,
  LookupFilterContextType as ContextType,
} from '~/components/logic-expression/utils'

/** Minimal shape for a form input when building lookup filter maps (id → label). */
export type FormInputForFilter = { id: string; label: string | null }

/**
 * Maps used together to display lookup filter expressions in a human-readable way.
 * Shared by records/lookup (LookupRowSearch) and form-v2 so the detailed table
 * query can run only when the lookup is opened.
 */
export type LookupFilterFieldMaps = {
  fieldNameMap: Record<string, string>
  fieldContextNameMap: Record<string, string>
  fieldContextTypeMap: Record<string, LookupFilterContextType>
}

/**
 * Builds the table-only part of LookupFilterFieldMaps (main, parent, child columns).
 * Merge with form maps in the consumer when you have both.
 */
export function getLookupFilterFieldNameMapTablePart(
  table: GetDetailedTableQuery['table'],
  t: (key: i18NextKeys, options?: TOptions) => string,
): LookupFilterFieldMaps {
  const fieldNameMap: Record<string, string> = {}
  const fieldContextNameMap: Record<string, string> = {}
  const fieldContextTypeMap: Record<string, LookupFilterContextType> = {}
  const tableName = table.name
  const parentTableName = table.parentTable?.name
  const parentColumns = table.parentTable?.columns ?? []

  table.columns.forEach((c) => {
    fieldNameMap[c.id] = c.name
    fieldContextNameMap[c.id] = t('record.lookup.filter_context.table', { name: tableName })
    fieldContextTypeMap[c.id] = ContextType.CURRENT_TABLE
  })
  parentColumns.forEach((c) => {
    fieldNameMap[c.id] = c.name
    fieldContextNameMap[c.id] = parentTableName
      ? t('record.lookup.filter_context.parent_table', { name: parentTableName })
      : t('record.lookup.filter_context.parent_table_only')
    fieldContextTypeMap[c.id] = ContextType.PARENT_TABLE
  })
  table.childTables.forEach((childTable) => {
    childTable.columns.forEach((c) => {
      fieldNameMap[c.id] = c.name
      fieldContextNameMap[c.id] = t('record.lookup.filter_context.child_table', {
        name: childTable.name,
      })
      fieldContextTypeMap[c.id] = ContextType.CHILD_TABLE
    })
  })
  return { fieldNameMap, fieldContextNameMap, fieldContextTypeMap }
}

/** Merges form and table lookup filter maps (table entries take precedence on key overlap). */
export function mergeLookupFilterFieldMaps(
  form: LookupFilterFieldMaps,
  table: LookupFilterFieldMaps,
): LookupFilterFieldMaps {
  return {
    fieldNameMap: { ...form.fieldNameMap, ...table.fieldNameMap },
    fieldContextNameMap: { ...form.fieldContextNameMap, ...table.fieldContextNameMap },
    fieldContextTypeMap: { ...form.fieldContextTypeMap, ...table.fieldContextTypeMap },
  }
}

/**
 * Builds the form-only part of LookupFilterFieldMaps (no table columns).
 * Compute once in FormFiller and pass down so each LookupInput doesn't rebuild from the full array.
 */
export function getFormLookupFilterFieldMaps(
  formInputs: FormInputForFilter[],
  t: (key: i18NextKeys, options?: TOptions) => string,
): LookupFilterFieldMaps {
  const fieldNameMap = Object.fromEntries(formInputs.map((inp) => [inp.id, inp.label ?? inp.id]))
  const fieldContextNameMap: Record<string, string> = {}
  const fieldContextTypeMap: Record<string, LookupFilterContextType> = {}
  formInputs.forEach((inp) => {
    fieldContextNameMap[inp.id] = t('record.lookup.filter_context.form_input')
    fieldContextTypeMap[inp.id] = ContextType.FORM
  })
  return { fieldNameMap, fieldContextNameMap, fieldContextTypeMap }
}

/**
 * Builds full LookupFilterFieldMaps from form inputs and optional table (e.g. for lookup cell
 * or when not using the form-only/table-part split).
 */
export function getLookupFilterFieldNameMap(
  formInputs: FormInputForFilter[],
  table: GetDetailedTableQuery['table'] | null | undefined,
  t: (key: i18NextKeys, options?: TOptions) => string,
): LookupFilterFieldMaps | undefined {
  const formMaps = getFormLookupFilterFieldMaps(formInputs, t)
  if (Object.keys(formMaps.fieldNameMap).length === 0 && !table) {
    return undefined
  }
  if (!table) {
    return formMaps
  }
  const tableMaps = getLookupFilterFieldNameMapTablePart(table, t)
  return mergeLookupFilterFieldMaps(formMaps, tableMaps)
}
